import { HeroBlock } from './HeroBlock';
import { HeroSlideshowBlock } from './HeroSlideshowBlock';
import { FeatureGridBlock } from './FeatureGridBlock';
import { TestimonialBlock } from './TestimonialBlock';
import { CtaBlock } from './CtaBlock';
import { FaqBlock } from './FaqBlock';
import { PinnedRevealBlock } from './PinnedRevealBlock';
import { HorizontalScrollBlock } from './HorizontalScrollBlock';
import { ScrollShowcaseBlock } from './ScrollShowcaseBlock';
import { MapBlock } from './MapBlock';
import { NewsletterBlock } from './NewsletterBlock';
import { ContactFormBlock } from './ContactFormBlock';
import { LegalContentBlock } from './LegalContentBlock';
import { GoogleReviewsBlock } from './GoogleReviewsBlock';
import { ScrollRevealBlock } from './ScrollRevealBlock';
import { MenuDownloadBlock } from './MenuDownloadBlock';
import { SPACING_OPTIONS } from '@/utils/spacing';
import { LOCALES } from '@/i18n/config';

// Campo margine riusato su ogni blocco "a sezione normale" (non su hero — a
// tutto schermo — né sugli scroll pinnati/orizzontali, dove lo spacing è
// parte della meccanica GSAP, non un margine decorativo).
const SPACING_FIELD = { key: 'spacing', label: 'Margine verticale', type: 'select', options: SPACING_OPTIONS };

// Riusate da ScrollRevealBlock per immagine e testo — stesso significato
// ovunque compaiano (posizione orizzontale dell'elemento nella sua riga).
const POSITION_OPTIONS = [
  { value: 'left', label: 'Sinistra' },
  { value: 'center', label: 'Centro' },
  { value: 'right', label: 'Destra' },
];

// Stesso set di valori per animIn e animOut: il significato è sempre "il
// lato/lo stile da cui l'elemento appare o verso cui scompare" (vedi
// useScrollReveal in useScrollPin.js) — mai invertito arbitrariamente tra
// i due campi.
const REVEAL_ANIM_OPTIONS = [
  { value: 'none', label: 'Nessuna' },
  { value: 'fade', label: 'Dissolvenza' },
  { value: 'scale', label: 'Scala' },
  { value: 'top', label: 'Verticale (dall\'alto)' },
  { value: 'bottom', label: 'Verticale (dal basso)' },
  { value: 'left', label: 'Orizzontale (da sinistra)' },
  { value: 'right', label: 'Orizzontale (da destra)' },
];

const MEDIA_TYPE_OPTIONS = [
  { value: 'image', label: 'Immagine' },
  { value: 'video', label: 'Video (YouTube/Vimeo)' },
];

/**
 * Campi immagine-o-video condivisi da ogni blocco/voce "a slide" (Hero,
 * HeroSlideshow, PinnedReveal, HorizontalScroll, ScrollShowcase) — stessa
 * forma dati ovunque, così SlideMedia.jsx (il componente di rendering) e
 * showIf in BlockForm.jsx bastano una volta sola per tutti i blocchi invece
 * di essere reimplementati per ciascuno. `showOverlay` è lo switch "con
 * testo e oscuramento" (comportamento di sempre) vs "media pulito, senza
 * testo né scrim" — stesso significato ovunque compaia.
 */
function mediaFields({ imageLabel = 'Immagine', altFallbackHint, square = false } = {}) {
  return [
    { key: 'mediaType', label: 'Tipo media', type: 'select', options: MEDIA_TYPE_OPTIONS },
    {
      key: 'image', type: 'image', square,
      label: square ? `${imageLabel} (quadrata — ritagliata automaticamente)` : imageLabel,
      showIf: { key: 'mediaType', equals: 'image' },
    },
    {
      key: 'imageAlt', type: 'text', i18n: true,
      label: `Testo alternativo immagine${altFallbackHint ? ` (vuoto = usa ${altFallbackHint})` : ''}`,
      showIf: { key: 'mediaType', equals: 'image' },
    },
    {
      key: 'videoUrl', label: 'URL video (YouTube o Vimeo) — riempie lo spazio come lo sfondo, muto e in loop', type: 'text',
      showIf: { key: 'mediaType', equals: 'video' },
    },
    { key: 'showOverlay', label: 'Testo e leggero oscuramento sopra il media (disattiva per un media "pulito", senza testo)', type: 'checkbox' },
  ];
}

const MEDIA_DEFAULTS = { mediaType: 'image', image: '', imageAlt: '', videoUrl: '', showOverlay: true };

/**
 * Registro dei blocchi disponibili per l'admin a blocchi e il BlockRenderer.
 * Ogni voce: label (UI), component (React), fields (schema per il form admin),
 * defaultProps. Tipi di campo:
 *   text | textarea | number | checkbox | lines (array di stringhe)
 *   select  — dropdown, richiede `options: [{ value, label }]`
 *   color   — swatch nativo + hex, vuoto = default del tema
 *   image   — singola immagine, drag-drop/upload/libreria
 *   images  — array di immagini, drag-drop/upload/libreria/riordino
 *   items   — lista di card ripetibili (itemFields ne definisce la forma),
 *             con drag-drop/upload/riordino/aggiungi/rimuovi per ogni card
 *             (supporta nesting: un itemField può essere a sua volta 'items')
 * `i18n: true` su un campo text/textarea = tradotto per lingua (vedi
 * src/utils/localize.js) — il valore salvato diventa { it: '...', en: '...' }
 * invece di stringa. Non mettere i18n su href/slug/numeri/colori.
 * `headingField` = chiave del campo che è il "titolo di blocco": BlockRenderer
 * lo usa per assegnare H1/H2 in base alla posizione in pagina (vedi
 * BlockRenderer.jsx) — solo i blocchi con un titolo unico e prominente
 * competono per l'H1 di pagina; blocchi senza (testimonial, pinnedReveal,
 * horizontalScroll) restano sempre a un livello fisso e sicuro.
 */
export const BLOCK_REGISTRY = {
  hero: {
    label: 'Hero',
    description: 'Sezione di apertura a tutta pagina con titolo, sottotitolo e un\'immagine di sfondo.',
    component: HeroBlock,
    headingField: 'title',
    fields: [
      ...mediaFields({ imageLabel: 'Sfondo (opzionale)', altFallbackHint: 'il Titolo' }),
      { key: 'eyebrow', label: 'Eyebrow', type: 'text', i18n: true },
      { key: 'title', label: 'Titolo', type: 'textarea', i18n: true },
      { key: 'subtitle', label: 'Sottotitolo', type: 'textarea', i18n: true },
      { key: 'cta', label: 'Testo CTA', type: 'text', i18n: true },
      { key: 'ctaHref', label: 'Link CTA', type: 'text' },
    ],
    defaultProps: { ...MEDIA_DEFAULTS, eyebrow: '', title: 'Titolo', subtitle: '', cta: 'Scopri', ctaHref: '#' },
  },
  heroSlideshow: {
    label: 'Hero con slideshow',
    description: 'Come l\'Hero, ma con più slide (immagine o video) di sfondo in crossfade automatico.',
    component: HeroSlideshowBlock,
    headingField: 'title',
    fields: [
      {
        key: 'slides', label: 'Slide di sfondo', type: 'items',
        itemFields: mediaFields({ altFallbackHint: 'il Titolo' }),
        itemDefault: { ...MEDIA_DEFAULTS },
      },
      { key: 'eyebrow', label: 'Eyebrow', type: 'text', i18n: true },
      { key: 'title', label: 'Titolo', type: 'textarea', i18n: true },
      { key: 'subtitle', label: 'Sottotitolo', type: 'textarea', i18n: true },
      { key: 'cta', label: 'Testo CTA', type: 'text', i18n: true },
      { key: 'ctaHref', label: 'Link CTA', type: 'text' },
      { key: 'interval', label: 'Intervallo slide (ms, solo tra immagini — un video dura quanto il video)', type: 'number' },
      { key: 'autoplay', label: 'Autoplay', type: 'checkbox' },
    ],
    defaultProps: {
      slides: [{ ...MEDIA_DEFAULTS }, { ...MEDIA_DEFAULTS }],
      eyebrow: '', title: 'Titolo', subtitle: '',
      cta: 'Scopri', ctaHref: '#', interval: 5000, autoplay: true,
    },
  },
  featureGrid: {
    label: 'Griglia feature',
    description: 'Titolo + griglia di voci con immagine, testo e link opzionale (es. servizi, prodotti).',
    component: FeatureGridBlock,
    headingField: 'title',
    fields: [
      { key: 'title', label: 'Titolo', type: 'text', i18n: true },
      { key: 'subtitle', label: 'Sottotitolo', type: 'textarea', i18n: true },
      {
        key: 'items', label: 'Voci', type: 'items',
        itemFields: [
          { key: 'image', label: 'Immagine', type: 'image' },
          { key: 'imageAlt', label: 'Testo alternativo immagine (vuoto = usa il titolo della voce)', type: 'text', i18n: true },
          { key: 'title', label: 'Titolo', type: 'text', i18n: true },
          { key: 'description', label: 'Testo', type: 'textarea', i18n: true },
          { key: 'ctaLabel', label: 'CTA testo (opzionale)', type: 'text', i18n: true },
          { key: 'ctaHref', label: 'CTA link (opzionale)', type: 'text' },
        ],
        itemDefault: { image: '', imageAlt: '', title: 'Nuova voce', description: '', ctaLabel: '', ctaHref: '' },
      },
      SPACING_FIELD,
    ],
    defaultProps: { title: 'Cosa offriamo', subtitle: '', items: [{ image: '', imageAlt: '', title: 'Voce 1', description: 'Descrizione.', ctaLabel: '', ctaHref: '' }], spacing: 'normal' },
  },
  testimonial: {
    label: 'Testimonianza',
    description: 'Citazione centrata con foto, autore e ruolo — recensioni o parole di un cliente.',
    component: TestimonialBlock,
    fields: [
      { key: 'image', label: 'Foto (opzionale)', type: 'image' },
      { key: 'imageAlt', label: 'Testo alternativo immagine (vuoto = usa il nome dell\'autore)', type: 'text', i18n: true },
      { key: 'quote', label: 'Citazione', type: 'textarea', i18n: true },
      { key: 'author', label: 'Autore', type: 'text' },
      { key: 'role', label: 'Ruolo', type: 'text', i18n: true },
      SPACING_FIELD,
    ],
    defaultProps: { image: '', imageAlt: '', quote: 'Citazione.', author: '', role: '', spacing: 'normal' },
  },
  cta: {
    label: 'Call to action',
    description: 'Blocco centrato con titolo e pulsante — invita a un\'azione (contatti, menu, prenotazione).',
    component: CtaBlock,
    headingField: 'title',
    fields: [
      { key: 'image', label: 'Sfondo (opzionale)', type: 'image' },
      { key: 'imageAlt', label: 'Testo alternativo immagine (vuoto = usa il Titolo)', type: 'text', i18n: true },
      { key: 'title', label: 'Titolo', type: 'text', i18n: true },
      { key: 'subtitle', label: 'Sottotitolo', type: 'textarea', i18n: true },
      { key: 'cta', label: 'Testo CTA', type: 'text', i18n: true },
      { key: 'ctaHref', label: 'Link CTA', type: 'text' },
      SPACING_FIELD,
    ],
    defaultProps: { image: '', imageAlt: '', title: 'Pronti a iniziare?', subtitle: '', cta: 'Contattaci', ctaHref: '#contatti', spacing: 'normal' },
  },
  faq: {
    label: 'FAQ',
    description: 'Elenco di domande frequenti ad accordion — si aprono/chiudono al click.',
    component: FaqBlock,
    headingField: 'title',
    fields: [
      { key: 'title', label: 'Titolo', type: 'text', i18n: true },
      {
        key: 'items', label: 'Domande', type: 'items',
        itemFields: [
          { key: 'image', label: 'Immagine (opzionale)', type: 'image' },
          { key: 'imageAlt', label: 'Testo alternativo immagine (vuoto = usa la domanda)', type: 'text', i18n: true },
          { key: 'question', label: 'Domanda', type: 'text', i18n: true },
          { key: 'answer', label: 'Risposta', type: 'textarea', i18n: true },
          { key: 'ctaLabel', label: 'CTA testo (opzionale)', type: 'text', i18n: true },
          { key: 'ctaHref', label: 'CTA link (opzionale)', type: 'text' },
        ],
        itemDefault: { image: '', imageAlt: '', question: 'Nuova domanda?', answer: '', ctaLabel: '', ctaHref: '' },
      },
      SPACING_FIELD,
    ],
    defaultProps: { title: 'Domande frequenti', items: [{ image: '', imageAlt: '', question: 'Domanda?', answer: 'Risposta.', ctaLabel: '', ctaHref: '' }], spacing: 'normal' },
  },
  pinnedReveal: {
    label: 'Scroll pinnato (crossfade)',
    description: 'Più scene a schermo intero che si dissolvono l\'una nell\'altra mentre si scrolla, ferme in vista.',
    component: PinnedRevealBlock,
    fields: [
      {
        key: 'scenes', label: 'Scene', type: 'items',
        itemFields: [
          ...mediaFields({ altFallbackHint: 'il testo della scena' }),
          { key: 'title', label: 'Testo', type: 'text', i18n: true },
          { key: 'ctaLabel', label: 'CTA testo (opzionale)', type: 'text', i18n: true },
          { key: 'ctaHref', label: 'CTA link (opzionale)', type: 'text' },
        ],
        itemDefault: { ...MEDIA_DEFAULTS, title: 'Nuova scena', ctaLabel: '', ctaHref: '' },
      },
    ],
    defaultProps: {
      scenes: [
        { ...MEDIA_DEFAULTS, title: 'Prima scena', ctaLabel: '', ctaHref: '' },
        { ...MEDIA_DEFAULTS, title: 'Seconda scena', ctaLabel: '', ctaHref: '' },
      ],
    },
  },
  horizontalScroll: {
    label: 'Scroll orizzontale',
    description: 'Pannelli affiancati che scorrono in orizzontale mentre si scrolla la pagina.',
    component: HorizontalScrollBlock,
    fields: [
      {
        key: 'panels', label: 'Pannelli', type: 'items',
        itemFields: [
          ...mediaFields({ altFallbackHint: 'il titolo del pannello' }),
          { key: 'title', label: 'Titolo', type: 'text', i18n: true },
          { key: 'ctaLabel', label: 'CTA testo (opzionale)', type: 'text', i18n: true },
          { key: 'ctaHref', label: 'CTA link (opzionale)', type: 'text' },
        ],
        itemDefault: { ...MEDIA_DEFAULTS, title: 'Nuovo pannello', ctaLabel: '', ctaHref: '' },
      },
    ],
    defaultProps: {
      panels: [
        { ...MEDIA_DEFAULTS, title: 'Uno', ctaLabel: '', ctaHref: '' },
        { ...MEDIA_DEFAULTS, title: 'Due', ctaLabel: '', ctaHref: '' },
        { ...MEDIA_DEFAULTS, title: 'Tre', ctaLabel: '', ctaHref: '' },
      ],
    },
  },
  scrollShowcase: {
    label: 'Vetrina a scroll',
    description: 'Immagine quadrata fissa a lato, testo che scorre a fianco — l\'immagine cambia in dissolvenza a ogni slide (stile racconto a scroll).',
    component: ScrollShowcaseBlock,
    fields: [
      {
        key: 'slides', label: 'Slide', type: 'items',
        itemFields: [
          ...mediaFields({ altFallbackHint: 'il titolo della slide', square: true }),
          { key: 'title', label: 'Titolo', type: 'text', i18n: true },
          { key: 'text', label: 'Testo', type: 'textarea', i18n: true },
        ],
        itemDefault: { ...MEDIA_DEFAULTS, title: 'Nuova slide', text: '' },
      },
      {
        key: 'imageSide', label: 'Lato immagine', type: 'select',
        options: [
          { value: 'left', label: 'Sinistra' },
          { value: 'right', label: 'Destra' },
        ],
      },
      {
        key: 'textAlign', label: 'Allineamento testo', type: 'select',
        options: [
          { value: 'left', label: 'Sinistra' },
          { value: 'center', label: 'Centro' },
          { value: 'right', label: 'Destra' },
        ],
      },
      SPACING_FIELD,
    ],
    defaultProps: {
      slides: [
        { ...MEDIA_DEFAULTS, title: 'Prima slide', text: '' },
        { ...MEDIA_DEFAULTS, title: 'Seconda slide', text: '' },
      ],
      imageSide: 'left',
      textAlign: 'left',
      spacing: 'normal',
    },
  },
  map: {
    label: 'Mappa',
    description: 'Mappa OpenStreetMap interattiva con la posizione del locale (da Impostazioni → Dati attività).',
    component: MapBlock,
    headingField: 'title',
    fields: [
      { key: '_hint', label: 'Indirizzo e coordinate si impostano in Impostazioni → Dati attività (da lì anche il pulsante "Trova coordinate dall\'indirizzo") — qui solo il modo in cui la mappa viene mostrata.', type: 'note' },
      { key: 'title', label: 'Titolo (opzionale)', type: 'text', i18n: true },
      { key: 'subtitle', label: 'Testo sopra la mappa (opzionale)', type: 'textarea', i18n: true },
      { key: 'zoom', label: 'Livello di zoom (1-19, più alto = più vicino)', type: 'number' },
      { key: 'height', label: 'Altezza mappa (px)', type: 'number' },
      { key: 'showDirectionsButton', label: 'Mostra pulsante "Ottieni indicazioni"', type: 'checkbox' },
      {
        key: 'layout', label: 'Layout', type: 'select',
        options: [
          { value: 'grid', label: 'Grid (contenuta, angoli arrotondabili)' },
          { value: 'widescreen', label: 'Widescreen (a tutta larghezza, sempre squadrata)' },
        ],
      },
      { key: 'roundness', label: 'Arrotondamento angoli (px, solo layout Grid)', type: 'number' },
      SPACING_FIELD,
    ],
    defaultProps: { title: '', subtitle: '', zoom: 15, height: 420, showDirectionsButton: true, layout: 'grid', roundness: 12, spacing: 'normal' },
  },
  newsletter: {
    label: 'Newsletter',
    description: 'Modulo di iscrizione newsletter (Brevo) con consenso GDPR dedicato — invio inline, senza uscire dalla pagina.',
    component: NewsletterBlock,
    headingField: 'title',
    fields: [
      { key: '_hint', label: 'Richiede BREVO_API_KEY configurata sul server (vedi .env.example) — nessun dato sensibile negli altri campi qui sotto.', type: 'note' },
      { key: 'title', label: 'Titolo (opzionale)', type: 'text', i18n: true },
      { key: 'subtitle', label: 'Sottotitolo (opzionale)', type: 'textarea', i18n: true },
      { key: 'firstNamePlaceholder', label: 'Placeholder campo nome', type: 'text', i18n: true },
      { key: 'lastNamePlaceholder', label: 'Placeholder campo cognome', type: 'text', i18n: true },
      { key: 'placeholder', label: 'Placeholder campo email', type: 'text', i18n: true },
      { key: 'buttonLabel', label: 'Testo pulsante', type: 'text', i18n: true },
      { key: 'consentLabel', label: 'Testo consenso (checkbox obbligatoria)', type: 'textarea', i18n: true },
      { key: 'successMessage', label: 'Messaggio dopo iscrizione riuscita', type: 'text', i18n: true },
      {
        key: 'brevoListId',
        label: 'ID lista Brevo (vuoto = usa BREVO_LIST_ID dell\'ambiente)',
        type: 'text',
      },
      { key: 'brevoTag', label: 'Tag da assegnare agli iscritti (es. "bottega")', type: 'text' },
      {
        key: 'notifyEmails',
        label: 'Notifica nuove iscrizioni a questi indirizzi (uno per riga, vuoto = nessuna notifica)',
        type: 'lines',
      },
      SPACING_FIELD,
    ],
    defaultProps: {
      title: 'Resta aggiornato', subtitle: '',
      firstNamePlaceholder: 'Nome', lastNamePlaceholder: 'Cognome',
      placeholder: 'La tua email', buttonLabel: 'Iscriviti',
      consentLabel: 'Acconsento al trattamento dei dati per ricevere la newsletter.',
      successMessage: 'Iscrizione confermata — grazie!',
      brevoListId: '', brevoTag: '', notifyEmails: [],
      spacing: 'normal',
    },
  },
  contactForm: {
    label: 'Modulo contatti',
    description: 'Nome, email e messaggio — invia una vera email (SMTP o Brevo, scelto in Impostazioni → Modulo contatti).',
    component: ContactFormBlock,
    headingField: 'title',
    fields: [
      { key: '_hint', label: 'Il metodo di invio (SMTP interno o Brevo) si sceglie in Impostazioni → Modulo contatti. Credenziali sempre come variabili d\'ambiente, vedi .env.example.', type: 'note' },
      { key: 'title', label: 'Titolo (opzionale)', type: 'text', i18n: true },
      { key: 'subtitle', label: 'Sottotitolo (opzionale)', type: 'textarea', i18n: true },
      { key: 'namePlaceholder', label: 'Placeholder campo nome', type: 'text', i18n: true },
      { key: 'emailPlaceholder', label: 'Placeholder campo email', type: 'text', i18n: true },
      { key: 'messagePlaceholder', label: 'Placeholder campo messaggio', type: 'text', i18n: true },
      { key: 'buttonLabel', label: 'Testo pulsante', type: 'text', i18n: true },
      { key: 'successMessage', label: 'Messaggio dopo invio riuscito', type: 'text', i18n: true },
      SPACING_FIELD,
    ],
    defaultProps: {
      title: 'Contattaci', subtitle: '',
      namePlaceholder: 'Nome', emailPlaceholder: 'Email', messagePlaceholder: 'Messaggio',
      buttonLabel: 'Invia', successMessage: 'Messaggio inviato — ti risponderemo presto.',
      spacing: 'normal',
    },
  },
  googleReviews: {
    label: 'Recensioni Google',
    description: 'Voto medio, recensioni curate + QR code e pulsante per lasciare una recensione su Google.',
    component: GoogleReviewsBlock,
    headingField: 'title',
    fields: [
      { key: 'title', label: 'Titolo', type: 'text', i18n: true },
      { key: 'subtitle', label: 'Sottotitolo (opzionale)', type: 'textarea', i18n: true },
      { key: 'rating', label: 'Voto medio (es. 4.9, 0 = nascosto)', type: 'number' },
      { key: 'reviewCount', label: 'Numero recensioni totali (mostrato tra parentesi, 0 = nascosto)', type: 'number' },
      {
        key: 'reviews', label: 'Recensioni', type: 'items',
        itemFields: [
          { key: 'avatar', label: 'Foto autore (opzionale — vuoto = iniziali)', type: 'image' },
          { key: 'author', label: 'Nome autore', type: 'text' },
          { key: 'rating', label: 'Voto (1-5)', type: 'number' },
          { key: 'date', label: 'Data (testo libero, es. "2 settimane fa")', type: 'text', i18n: true },
          { key: 'text', label: 'Testo recensione', type: 'textarea', i18n: true },
        ],
        itemDefault: { avatar: '', author: 'Nome Cognome', rating: 5, date: '', text: '' },
      },
      { key: 'reviewLink', label: 'Link per lasciare una recensione Google (usato da QR code e pulsante)', type: 'text' },
      { key: 'ctaLabel', label: 'Testo pulsante (vuoto = nessun pulsante)', type: 'text', i18n: true },
      { key: 'showQrCode', label: 'Mostra QR code', type: 'checkbox' },
      SPACING_FIELD,
    ],
    defaultProps: {
      title: 'Cosa dicono di noi su Google',
      subtitle: '',
      rating: 4.9,
      reviewCount: 0,
      reviews: [{ avatar: '', author: 'Nome Cognome', rating: 5, date: '', text: 'Una bellissima esperienza.' }],
      reviewLink: '',
      ctaLabel: 'Lascia una recensione',
      showQrCode: true,
      spacing: 'normal',
    },
  },
  scrollReveal: {
    label: 'Immagine + testo animati',
    description: 'Immagine e testo indipendenti (mai a sfondo), ognuno con dimensione/posizione/margini propri e animazione controllabile all\'ingresso e all\'uscita dal viewport.',
    component: ScrollRevealBlock,
    fields: [
      { key: 'image', label: 'Immagine', type: 'image' },
      { key: 'imageAlt', label: 'Testo alternativo immagine', type: 'text', i18n: true },
      { key: 'imageWidth', label: 'Immagine — larghezza (% del blocco, 1-100)', type: 'number' },
      { key: 'imagePosition', label: 'Immagine — posizione orizzontale', type: 'select', options: POSITION_OPTIONS },
      { key: 'imageMarginTop', label: 'Immagine — margine superiore (px)', type: 'number' },
      { key: 'imageMarginRight', label: 'Immagine — margine destro (px)', type: 'number' },
      { key: 'imageMarginBottom', label: 'Immagine — margine inferiore (px)', type: 'number' },
      { key: 'imageMarginLeft', label: 'Immagine — margine sinistro (px)', type: 'number' },
      { key: 'imageAnimIn', label: 'Immagine — animazione iniziale (comparsa)', type: 'select', options: REVEAL_ANIM_OPTIONS },
      { key: 'imageAnimOut', label: 'Immagine — animazione finale (scomparsa)', type: 'select', options: REVEAL_ANIM_OPTIONS },
      { key: 'text', label: 'Testo', type: 'textarea', i18n: true },
      { key: '_textMarkupHint', label: 'Nel testo: **grassetto**, *corsivo*, invio = a capo.', type: 'note' },
      { key: 'textSize', label: 'Testo — dimensione', type: 'select', options: [
        { value: 'body', label: 'Normale' },
        { value: 'body-lg', label: 'Grande' },
        { value: 'h3', label: 'Molto grande' },
        { value: 'h2', label: 'Enorme' },
        { value: 'h1', label: 'Massima' },
      ] },
      { key: 'textWidth', label: 'Testo — larghezza', type: 'select', options: [
        { value: 'grid', label: 'Grid (larghezza di lettura contenuta)' },
        { value: 'widescreen', label: 'Widescreen (tutta la larghezza del blocco)' },
        { value: 'custom', label: 'Personalizzata' },
      ] },
      {
        key: 'textWidthCustom', label: 'Testo — larghezza personalizzata (% del blocco, 1-100)', type: 'number',
        showIf: { key: 'textWidth', equals: 'custom' },
      },
      { key: 'textPosition', label: 'Testo — posizione orizzontale', type: 'select', options: POSITION_OPTIONS },
      { key: 'textAlign', label: 'Testo — allineamento', type: 'select', options: [
        { value: 'left', label: 'Sinistra' },
        { value: 'center', label: 'Centro' },
        { value: 'right', label: 'Destra' },
        { value: 'justify', label: 'Giustificato' },
      ] },
      { key: 'textAnimIn', label: 'Testo — animazione iniziale (comparsa)', type: 'select', options: REVEAL_ANIM_OPTIONS },
      { key: 'textAnimOut', label: 'Testo — animazione finale (scomparsa)', type: 'select', options: REVEAL_ANIM_OPTIONS },
      { key: 'marginTop', label: 'Margine superiore blocco (px)', type: 'number' },
      { key: 'marginBottom', label: 'Margine inferiore blocco (px)', type: 'number' },
      { key: 'bgColor', label: 'Colore di sfondo blocco', type: 'color' },
      { key: 'textColor', label: 'Colore testo', type: 'color' },
    ],
    defaultProps: {
      image: '', imageAlt: '', imageWidth: 50, imagePosition: 'center',
      imageMarginTop: 0, imageMarginRight: 0, imageMarginBottom: 0, imageMarginLeft: 0,
      imageAnimIn: 'fade', imageAnimOut: 'fade',
      text: 'Testo di esempio.', textSize: 'body-lg', textWidth: 'grid', textWidthCustom: 60, textPosition: 'center', textAlign: 'center',
      textAnimIn: 'fade', textAnimOut: 'fade',
      marginTop: 96, marginBottom: 96, bgColor: '', textColor: '',
    },
  },
  menuDownload: {
    label: 'Menu PDF',
    description: 'Un pulsante "Vedi il menu" che apre il PDF in overlay (su mobile nel viewer nativo) — il file mostrato segue automaticamente la lingua del sito.',
    component: MenuDownloadBlock,
    headingField: 'title',
    fields: [
      { key: 'title', label: 'Titolo (opzionale)', type: 'text', i18n: true },
      { key: 'subtitle', label: 'Sottotitolo (opzionale)', type: 'textarea', i18n: true },
      { key: 'buttonLabel', label: 'Testo pulsante (opzionale — default "Vedi il menu" / "View menu")', type: 'text', i18n: true },
      {
        key: 'items', label: 'File PDF per lingua', type: 'items',
        itemFields: [
          { key: 'locale', label: 'Lingua', type: 'select', options: LOCALES.map((l) => ({ value: l.code, label: l.label })) },
          { key: 'pdfFile', label: 'File PDF del menu', type: 'file' },
        ],
        itemDefault: { locale: LOCALES[0]?.code || 'it', pdfFile: '' },
      },
      SPACING_FIELD,
      { key: 'bgColor', label: 'Colore di sfondo blocco', type: 'color' },
      { key: 'textColor', label: 'Colore testo', type: 'color' },
      { key: 'buttonBgColor', label: 'Colore pulsante', type: 'color' },
      { key: 'buttonTextColor', label: 'Colore testo pulsante', type: 'color' },
    ],
    defaultProps: {
      title: 'Il nostro menu',
      subtitle: '',
      buttonLabel: '',
      items: LOCALES.map((l) => ({ locale: l.code, pdfFile: '' })),
      spacing: 'normal',
      bgColor: '',
      textColor: '',
      buttonBgColor: '',
      buttonTextColor: '',
    },
  },
  legalContent: {
    label: 'Testo legale',
    description: 'Titolo + sezioni di testo semplice — per pagine come Privacy, Termini e Condizioni, Cookie Policy.',
    component: LegalContentBlock,
    headingField: 'title',
    fields: [
      { key: 'title', label: 'Titolo', type: 'text', i18n: true },
      { key: 'updatedAt', label: 'Ultimo aggiornamento (testo libero, es. "Aggiornato il 9 agosto 2026")', type: 'text', i18n: true },
      {
        key: 'sections', label: 'Sezioni', type: 'items',
        itemFields: [
          { key: 'heading', label: 'Titolo sezione', type: 'text', i18n: true },
          { key: 'body', label: 'Testo (gli a capo diventano nuovi paragrafi)', type: 'textarea', i18n: true },
        ],
        itemDefault: { heading: 'Nuova sezione', body: '' },
      },
      SPACING_FIELD,
    ],
    defaultProps: { title: '', updatedAt: '', sections: [], spacing: 'normal' },
  },
};

export const BLOCK_TYPES = Object.keys(BLOCK_REGISTRY);
